/**
 * Pure helpers for the map zoom-and-pan feature.
 *
 * All geometry is expressed in viewBox units (not screen pixels) so the
 * math stays correct regardless of how the SVG is scaled to fit the
 * screen container. The PanResponder converts touch-pixel deltas to
 * viewBox-unit deltas before calling clampPan.
 *
 * Coordinate conventions
 * ──────────────────────
 * The SVG viewBox is `viewBoxX  viewBoxY  viewBoxW  viewBoxH`.
 * The inner <G transform="translate(tx, ty) scale(zoomScale)"> shifts
 * content so that "when you pan right" (finger moves right) tx increases.
 *
 * Valid tx range when zoomScale=s and viewBox spans [viewBoxX, viewBoxX+viewBoxW]:
 *   The scaled content is zoomScale × viewBoxW wide.
 *   The visible window inside that is viewBoxW/s wide.
 *   To never show blank space to the left:  tx ≤ 0
 *   To never show blank space to the right: tx ≥ viewBoxW × (1 − s)
 *   → clamped range: [viewBoxW * (1 - s), 0]   (both negative for s > 1)
 *
 * Same logic applies to ty with viewBoxH.
 *
 * Note: the origin of the translate is in viewBox-space, so tx / ty
 * values are in viewBox units, NOT pixels.
 */

export interface PanOffset {
  tx: number
  ty: number
}

/**
 * Convert a screen-pixel drag into a clamped viewBox-space offset.
 * Native gestures and the web pointer fallback use this exact math so
 * platform-specific event plumbing cannot make panning behave differently.
 */
export function panFromScreenDelta(
  viewBoxX: number,
  viewBoxY: number,
  viewBoxW: number,
  viewBoxH: number,
  zoomScale: number,
  containerWidth: number,
  base: PanOffset,
  screenDx: number,
  screenDy: number,
): PanOffset {
  const pixelsPerViewBoxUnit = containerWidth / viewBoxW
  if (!Number.isFinite(pixelsPerViewBoxUnit) || pixelsPerViewBoxUnit <= 0) {
    return clampPan(viewBoxX, viewBoxY, viewBoxW, viewBoxH, zoomScale, base.tx, base.ty)
  }
  return clampPan(
    viewBoxX,
    viewBoxY,
    viewBoxW,
    viewBoxH,
    zoomScale,
    base.tx + screenDx / pixelsPerViewBoxUnit,
    base.ty + screenDy / pixelsPerViewBoxUnit,
  )
}

/**
 * Zoom scale bounds. The map can be scaled between 1× (full board, no zoom)
 * and MAX, in ZOOM_STEP increments via the on-map +/- buttons. DEFAULT is the
 * scale applied the first time the player enters zoom mode.
 */
export const MIN_ZOOM_SCALE = 1.0
export const MAX_ZOOM_SCALE = 3.0
export const DEFAULT_ZOOM_SCALE = 1.7
export const ZOOM_STEP = 0.5

/** Clamp a requested zoom scale into [MIN_ZOOM_SCALE, MAX_ZOOM_SCALE]. */
export function clampZoomScale(scale: number): number {
  return Math.min(MAX_ZOOM_SCALE, Math.max(MIN_ZOOM_SCALE, scale))
}

/**
 * Recompute the pan offset when the zoom scale changes so the point currently
 * at the centre of the visible window stays put (zoom about the centre rather
 * than the origin). Returns the new, already-clamped pan offset.
 *
 * Derivation: the transform maps a content point p to `p*s + t`. The visible
 * window centre is the fixed coordinate C = viewBoxOrigin + extent/2. The
 * content point at the centre now is `p_c = (C − t)/oldScale`; to keep p_c at
 * the centre after the scale change: `t' = C − p_c*newScale`.
 */
export function rescaleAboutCenter(
  viewBoxX: number,
  viewBoxY: number,
  viewBoxW: number,
  viewBoxH: number,
  oldScale: number,
  newScale: number,
  pan: PanOffset,
): PanOffset {
  const cx = viewBoxX + viewBoxW / 2
  const cy = viewBoxY + viewBoxH / 2
  const ratio = newScale / oldScale
  const tx = cx - (cx - pan.tx) * ratio
  const ty = cy - (cy - pan.ty) * ratio
  return clampPan(viewBoxX, viewBoxY, viewBoxW, viewBoxH, newScale, tx, ty)
}

/**
 * Clamp a requested (tx, ty) pan offset so the scaled map never
 * exposes a blank region outside the original viewBox boundaries.
 *
 * For a viewBox starting at (viewBoxX, viewBoxY) with the transform
 * `translate(tx, ty) scale(s)`, a point (px, py) in original coords
 * renders at (px*s + tx, py*s + ty). For the content edges to align
 * with the viewBox edges:
 *   left:  viewBoxX*s + tx = viewBoxX                  → tx_max = viewBoxX(1−s)
 *   right: (viewBoxX+W)*s + tx = viewBoxX+W            → tx_min = (viewBoxX+W)(1−s)
 * Symmetric in y. Both bounds are ≤ 0 when s > 1.
 *
 * @param viewBoxX   Origin x of the SVG viewBox (in viewBox units).
 * @param viewBoxY   Origin y of the SVG viewBox (in viewBox units).
 * @param viewBoxW   Width of the SVG viewBox (in viewBox units).
 * @param viewBoxH   Height of the SVG viewBox (in viewBox units).
 * @param zoomScale  The scale factor applied to the content group (e.g. 1.7).
 * @param tx         Requested horizontal translate in viewBox units.
 * @param ty         Requested vertical translate in viewBox units.
 * @returns Clamped { tx, ty } safe to apply as the translate values.
 */
export function clampPan(
  viewBoxX: number,
  viewBoxY: number,
  viewBoxW: number,
  viewBoxH: number,
  zoomScale: number,
  tx: number,
  ty: number,
): PanOffset {
  if (zoomScale <= 1) {
    // No zoom — no panning needed; identity position.
    return { tx: 0, ty: 0 }
  }
  const maxTx = viewBoxX * (1 - zoomScale)
  const minTx = (viewBoxX + viewBoxW) * (1 - zoomScale)
  const maxTy = viewBoxY * (1 - zoomScale)
  const minTy = (viewBoxY + viewBoxH) * (1 - zoomScale)
  // The `+ 0` normalises -0 to +0 so callers comparing with `Object.is`
  // or `===` don't see surprising sign differences when viewBox starts
  // at the origin.
  return {
    tx: Math.min(maxTx, Math.max(minTx, tx)) + 0,
    ty: Math.min(maxTy, Math.max(minTy, ty)) + 0,
  }
}

/**
 * Compute the initial pan offset that centres the visible region on a
 * given focus point (in viewBox units). Used when zoom is first enabled
 * with a selected territory, so the player lands on the relevant area.
 *
 * If no focus point is provided the function centres on the viewBox
 * origin (shows the top-left quarter of the map, matching where most
 * maps place their densest territory clusters).
 *
 * The return value is already clamped.
 *
 * @param viewBoxW   Width of the SVG viewBox (in viewBox units).
 * @param viewBoxH   Height of the SVG viewBox (in viewBox units).
 * @param zoomScale  The scale factor applied to the content group.
 * @param focus      Optional { x, y } in viewBox units to centre on.
 * @returns Clamped { tx, ty } pan offset in viewBox units.
 */
export function initialPanForFocus(
  viewBoxX: number,
  viewBoxY: number,
  viewBoxW: number,
  viewBoxH: number,
  zoomScale: number,
  focus?: { x: number; y: number } | null,
): PanOffset {
  if (zoomScale <= 1) return { tx: 0, ty: 0 }

  // Transform: translate(tx, ty) scale(zoomScale). A point (px, py)
  // renders at (px*s + tx, py*s + ty). For the focus to land at the
  // visible-window centre (viewBoxX + W/2, viewBoxY + H/2):
  //   focus.x*s + tx = viewBoxX + W/2  →  tx = viewBoxX + W/2 − focus.x*s
  //
  // No focus → centre on the viewBox's own midpoint, which simplifies
  // back into the same formula with focus = midpoint.
  const focusX = focus?.x ?? viewBoxX + viewBoxW / 2
  const focusY = focus?.y ?? viewBoxY + viewBoxH / 2

  const tx = viewBoxX + viewBoxW / 2 - focusX * zoomScale
  const ty = viewBoxY + viewBoxH / 2 - focusY * zoomScale
  return clampPan(viewBoxX, viewBoxY, viewBoxW, viewBoxH, zoomScale, tx, ty)
}
